import React, { useEffect, useRef, useState } from 'react';
import { Send, Square, Upload, X } from 'lucide-react';
import { SkillPendingAsk } from '@/types';
import { processAttachments } from '@/components/agent/utils/streamSession';
import { useAgentStore } from '@/stores/agentStore';

/** 品牌色（与 PipelineView / SkillPanel 同源） */
const ACCENT = '#6D5BF5';

/** 询问会话动作桥（模块级注册表，同 FlowEditor isResizingAnyNode 先例）：
 * DetailPageContent 渲染期按节点 id 注册挂起会话的最新闭包，DetailPageNode 经弹窗回调按 id 消费。
 * 不用单例 ref：多详情页节点时无挂起节点的渲染会覆盖单例，导致挂起节点的回复路由到错误会话（静默丢失 + 弹窗死锁） */
export interface DetailPageAskActions {
  reply: (text: string, attachments?: Array<Record<string, any>>, displayText?: string) => void;
  cancel: () => void;
}
export const askActionRegistry = new Map<string, DetailPageAskActions>();

/** 询问快照：仅在 pendingAsk 引用变化时由 DetailPageContent 上报，驱动外壳弹窗与 zIndex 抬升 */
export interface DetailPageAskSnapshot {
  kind: 'planner' | 'designer';
  ask: SkillPendingAsk;
}

interface DetailPageAskPopoverProps {
  ask: SkillPendingAsk;
  kind: 'planner' | 'designer';
  /** 交互提交（选项/文本/上传共用；displayText 透传后端存 answer） */
  onReply: (reply: string, attachments?: Array<Record<string, any>>, displayText?: string) => void;
  /** 取消（调 workflowCancel，状态回 idle 后随数据流自动收起） */
  onCancel: () => void;
}

/** 本地上传项（提交前暂存；提交时经 processAttachments 转可序列化 payload） */
interface UploadItem {
  file: File;
  name: string;
  type: string;
  size: number;
  thumbnailUrl?: string;
}

/**
 * 节点底部询问弹窗（§4.5）：渲染于 DetailPageNode 根 div 内、卡片之后，悬于节点下边缘
 * - pendingAsk 出现即弹出、应答/取消后由数据流自动收起；不做点击外部关闭（保证必答）；
 * - 选项按钮 / 文本输入 / 图片上传能力自 DetailPageSkillPanel 整体迁入，交互状态为组件局部 state；
 * - 挂起期间节点 zIndex 抬升由 DetailPageNode 负责（E9），弹窗自身 z-30 保证节点内最上层。
 */
export const DetailPageAskPopover: React.FC<DetailPageAskPopoverProps> = ({
  ask,
  kind,
  onReply,
  onCancel,
}) => {
  const [textInput, setTextInput] = useState('');
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const projectId = useAgentStore((s) => s.projectId);

  // 交互卡切换（提交后 pendingAsk 置空/新卡到来）时重置本地输入态
  useEffect(() => {
    setTextInput('');
    setSubmitting(false);
    setUploads((prev) => {
      prev.forEach((u) => { if (u.thumbnailUrl) URL.revokeObjectURL(u.thumbnailUrl); });
      return [];
    });
  }, [ask]);

  const handleOption = (option: { label: string; replyText?: string }) => {
    if (submitting) return;
    setSubmitting(true);
    onReply(option.replyText ?? option.label, undefined, option.label);
  };

  const handleTextSubmit = () => {
    const trimmed = textInput.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    onReply(trimmed, undefined, trimmed);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const items: UploadItem[] = Array.from(files).map((file) => (
      file.type.startsWith('image/')
        ? { file, name: file.name, type: file.type, size: file.size, thumbnailUrl: URL.createObjectURL(file) }
        : { file, name: file.name, type: file.type, size: file.size }
    ));
    setUploads((prev) => [...prev, ...items]);
    e.target.value = '';
  };

  const removeUpload = (index: number) => {
    setUploads((prev) => {
      const item = prev[index];
      if (item?.thumbnailUrl) URL.revokeObjectURL(item.thumbnailUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUploadSubmit = async () => {
    if (submitting || uploads.length === 0) return;
    setSubmitting(true);
    try {
      const processed = await processAttachments(uploads, [], projectId || undefined);
      const payloads = processed.attachmentPayloads
        .filter((p: any) => p.dataUrl && !p.dataUrl.startsWith('blob:'))
        .map((p: any) => ({ ...p, thumbnailUrl: undefined }));
      if (payloads.length === 0) {
        setSubmitting(false);
        return;
      }
      onReply('', payloads);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-kind={kind}
      className="absolute left-4 right-4 z-30 max-h-[480px] rounded-[24px] bg-[#232326] border flex flex-col overflow-hidden"
      style={{
        top: 'calc(100% + 12px)',
        borderColor: `${ACCENT}99`,
        boxShadow: '0 16px 48px -12px rgba(109, 91, 245, 0.45)',
        animation: 'detailpage-ask-in 200ms ease-out',
      }}
      onDoubleClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <style>{'@keyframes detailpage-ask-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }'}</style>

      {/* 头部：状态圆点 + 标题 + 取消 */}
      <div className="flex items-center gap-3 px-6 h-14 shrink-0 border-b border-white/[0.08]">
        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: ACCENT }} />
        <span className="flex-1 min-w-0 truncate text-[22px] text-white/90">{ask.title || '请确认'}</span>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex items-center gap-2 px-4 h-9 rounded-xl bg-white/[0.06] border border-white/[0.12] text-[20px] text-white/70 hover:bg-white/[0.12] hover:text-white/90 transition-all shrink-0 disabled:opacity-40"
        >
          <Square size={16} />
          取消
        </button>
      </div>

      {/* 主体：选项按钮 / 文本输入 / 上传区（超限内部滚动） */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-4">
        <PendingAskBody
          ask={ask}
          textInput={textInput}
          submitting={submitting}
          uploads={uploads}
          onTextInput={setTextInput}
          onOption={handleOption}
          onTextSubmit={handleTextSubmit}
          onFileSelect={handleFileSelect}
          onUploadSubmit={handleUploadSubmit}
          onRemoveUpload={removeUpload}
          fileInputRef={fileInputRef}
        />
      </div>
    </div>
  );
};

interface PendingAskBodyProps {
  ask: SkillPendingAsk;
  textInput: string;
  submitting: boolean;
  uploads: UploadItem[];
  onTextInput: (value: string) => void;
  onOption: (option: { label: string; replyText?: string }) => void;
  onTextSubmit: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUploadSubmit: () => void;
  onRemoveUpload: (index: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

/** 交互卡主体（选项按钮 / 文本输入 / 上传区），独立组件便于按字段 memo */
const PendingAskBody = React.memo<PendingAskBodyProps>(({
  ask,
  textInput,
  submitting,
  uploads,
  onTextInput,
  onOption,
  onTextSubmit,
  onFileSelect,
  onUploadSubmit,
  onRemoveUpload,
  fileInputRef,
}) => (
  <>
    {ask.options.length > 0 && (
      <div className="flex flex-col gap-3">
        {ask.options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onOption(option)}
            disabled={submitting}
            className="px-6 h-14 rounded-2xl bg-white/[0.06] border text-[22px] text-white/85 text-left transition-all disabled:opacity-40 hover:bg-[#6D5BF5] hover:border-transparent hover:text-white"
            style={{ borderColor: `${ACCENT}66` }}
          >
            {option.label}
          </button>
        ))}
      </div>
    )}

    {ask.allowTextInput && (
      <div className="flex items-center gap-3 pl-5 pr-3 h-14 rounded-2xl bg-white/[0.06] border border-white/[0.12] focus-within:border-white/25 transition-colors">
        <input
          type="text"
          value={textInput}
          onChange={(e) => onTextInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              onTextSubmit();
            }
          }}
          placeholder={ask.placeholder || '请输入你的回答…'}
          disabled={submitting}
          className="flex-1 bg-transparent text-[22px] text-white/90 placeholder:text-white/30 focus:outline-none disabled:opacity-40 min-w-0"
        />
        <button
          type="button"
          onClick={onTextSubmit}
          disabled={submitting || !textInput.trim()}
          className="w-11 h-11 rounded-xl bg-white text-[#18181b] flex items-center justify-center hover:bg-white/90 transition-colors disabled:opacity-30 shrink-0"
        >
          <Send size={20} />
        </button>
      </div>
    )}

    {ask.allowUpload && (
      <div className="flex flex-col gap-3">
        {uploads.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {uploads.map((item, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden bg-white/[0.04] border border-white/[0.10]">
                {item.thumbnailUrl ? (
                  <img src={item.thumbnailUrl} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[16px] text-white/40 px-1 text-center break-all">
                    {item.name}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveUpload(idx)}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center text-white/80 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-3 h-14 rounded-2xl bg-white/[0.04] border-2 border-dashed border-white/[0.14] text-[22px] text-white/60 hover:bg-white/[0.08] hover:border-white/[0.25] transition-all disabled:opacity-40"
          >
            <Upload size={22} />
            {uploads.length > 0 ? `已选 ${uploads.length} 张 · 点击继续添加` : '点击上传图片'}
          </button>
          {uploads.length > 0 && (
            <button
              type="button"
              onClick={onUploadSubmit}
              disabled={submitting}
              className="px-6 h-14 rounded-2xl bg-white text-[#18181b] text-[22px] font-medium hover:bg-white/90 transition-colors disabled:opacity-40 shrink-0"
            >
              发送图片
            </button>
          )}
        </div>
      </div>
    )}
  </>
));
